import { Task } from "@lit/task";
import {
    DASHBOARD_CONFIG,
    type EntityFilter,
    formatDate,
    type JobApplicationDTO,
    JobApplicationStatus,
    type JobPostingDTO,
} from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { alert } from "../../../../elements/alert-dialog";
import "../../../../elements/entity-filters";
import type { EntityFiltersEl } from "../../../../elements/entity-filters";
import "../../../../elements/new-employee-dialog";
import type { NewEmployeeDialog } from "../../../../elements/new-employee-dialog";
import { app, router } from "../../../../init";
import {
    acceptApplication,
    buildEmployeeDraftFromApplication,
    rejectApplication,
} from "../../../../lib/application-actions";
import { singleton } from "../../../../lib/singleton";
import { debounce } from "../../../../lib/util";
import { shared } from "../../../../styles";
import "../../../recruiting/components/job-application-dialog";
import type { JobApplicationDialog } from "../../../recruiting/components/job-application-dialog";
import "../../../recruiting/components/job-application-preview";
import { mergeDashboardSettings } from "../../utils/merge-settings";
import "./base-tile";

type ApplicationGroup = {
    posting: JobPostingDTO;
    applications: JobApplicationDTO[];
    newestApplicationAt: number;
};

@customElement("ptc-dashboard-tile-recruiting")
export class DashboardTileRecruiting extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.recruitingApplications.size;

    @state()
    private _filters: EntityFilter[] = app.settings.dashboard.tiles.recruitingApplications.settings?.filters ?? [];

    @singleton("ptc-job-application-dialog")
    private _jobApplicationDialog: JobApplicationDialog;

    @singleton("ptc-new-employee-dialog")
    private _newEmployeeDialog: NewEmployeeDialog;

    private _isHandlingApplication = false;

    get hasPendingSave() {
        return this._isHandlingApplication;
    }

    private _applicationsTask = new Task(this, {
        task: async () => {
            const postings = await app.api.getJobPostings({ includeArchived: false });

            return postings
                .filter((posting) => this._hasPostingAccess(posting))
                .map((posting) => {
                    const applications = (posting.applications || [])
                        .filter((application) => application.status === JobApplicationStatus.Open)
                        .sort((a, b) => b.created.getTime() - a.created.getTime());

                    return {
                        posting,
                        applications,
                        newestApplicationAt: applications[0]?.created?.getTime() || 0,
                    };
                })
                .filter((group) => group.applications.length)
                .sort((a, b) => b.newestApplicationAt - a.newestApplicationAt);
        },
        args: () => [],
    });

    private _persistFiltersNow(filters: EntityFilter[]) {
        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    recruitingApplications: {
                        settings: { filters },
                    },
                },
            }),
        }).catch((error) => {
            console.error(error);
        });
    }

    private _persistFilters = debounce((filters: EntityFilter[]) => this._persistFiltersNow(filters), 400) as (
        filters: EntityFilter[]
    ) => void;

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }

            ptc-dashboard-tile {
                --tile-content-padding: 0;
            }

            ptc-dashboard-tile:has(ptc-entity-filters:focus-within)::part(settings) {
                z-index: 4;
            }

            .content {
                display: flex;
                flex-direction: column;
                min-height: 100%;
            }

            .list {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                padding: 1rem;
            }

            .posting {
                position: relative;
                display: flex;
                flex-direction: column;
                border: 1px solid var(--shade-2);
                border-radius: var(--border-radius);
                overflow: hidden;
            }

            .posting-header {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                min-width: 0;
                padding: 0.75rem 1rem;
                border-bottom: 1px solid var(--shade-2);
            }

            .posting-title {
                flex: 1;
                margin: 0;
                font-size: 1.1em;
                font-weight: 600;
                line-height: 1.25;
                color: var(--color-text);
                display: -webkit-box;
                overflow: hidden;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
            }

            .posting-open {
                position: absolute;
                top: 0.6rem;
                right: 0.75rem;
                background: var(--color-bg);
                color: var(--tile-icon-color);
                border-color: var(--tile-icon-color);
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.15s ease;
            }

            .posting-header:hover .posting-open,
            .posting-open:focus-visible {
                opacity: 1;
                pointer-events: auto;
            }

            .posting-details {
                display: flex;
                flex-wrap: wrap;
                gap: 0.35rem;
            }

            .posting-detail {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
            }

            .applications {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                gap: 0.5rem;
                padding: 1rem;
            }

            ptc-job-application-preview::part(card) {
                border: 0;
                padding: 0.5rem;
            }

            ptc-job-application-preview::part(card):hover {
                background: var(--shade-1);
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 0.75rem;
                height: 100%;
                padding: 1.5rem;
                text-align: center;
                color: var(--color-text-secondary);
            }

            .empty-state ptc-ui-icon {
                font-size: 2rem;
                color: var(--orange);
            }

            .empty-title {
                font-weight: 600;
                color: var(--color-text);
            }
        `,
    ];

    private _hasPostingAccess(posting: JobPostingDTO) {
        const accessiblePosition = posting.positions?.some((position) => {
            if (!position?.id) {
                return false;
            }

            const resolvedPosition = app.getPosition(position.id)?.position;
            return !!resolvedPosition && app.hasAccess({ position: resolvedPosition });
        });

        if (accessiblePosition) {
            return true;
        }

        if (posting.positions?.length) {
            return false;
        }

        const venue = app.venues.find((entry) => entry.id === posting.venueId);
        return venue ? app.hasAccess({ venue }) : false;
    }

    private get _subLabel() {
        const groups = this._applicationsTask.value;
        if (!groups?.length) {
            return undefined;
        }
        const filtered = this._applyFilters(groups);
        const totalApplications = filtered.reduce((sum, group) => sum + group.applications.length, 0);
        if (totalApplications === 0) {
            return undefined;
        }
        const applicationsLabel = totalApplications === 1 ? "Bewerbung" : "Bewerbungen";
        const distinctVenues = new Set(filtered.map((group) => group.posting.venueId)).size;
        if (distinctVenues <= 1) {
            return `${totalApplications} ${applicationsLabel}`;
        }
        return `${totalApplications} ${applicationsLabel} für ${distinctVenues} Standorte`;
    }

    private _applyFilters(groups: ApplicationGroup[]) {
        const venueIds = new Set(
            this._filters.filter((filter) => filter.type === "venue").map((filter) => filter.value as number)
        );
        const positionIds = new Set(
            this._filters.filter((filter) => filter.type === "position").map((filter) => filter.value as number)
        );

        if (venueIds.size === 0 && positionIds.size === 0) {
            return groups;
        }

        return groups.filter((group) => {
            if (venueIds.size > 0 && !venueIds.has(group.posting.venueId)) {
                return false;
            }
            if (positionIds.size > 0) {
                const positions = group.posting.positions ?? [];
                if (!positions.some((position) => positionIds.has(position.id))) {
                    return false;
                }
            }
            return true;
        });
    }

    private async _openApplication(group: ApplicationGroup, application: JobApplicationDTO) {
        this._isHandlingApplication = true;
        try {
            const action = (
                await this._jobApplicationDialog.show({
                    application,
                    posting: group.posting,
                    showPostingLink: true,
                })
            )?.action;

            if (action === "approve") {
                const employee = await this._newEmployeeDialog.show(
                    buildEmployeeDraftFromApplication(application, group.posting)
                );

                if (employee) {
                    await this._acceptApplication(application.id, employee.id);
                    router.go(`employees/${employee.id}`);
                }
            } else if (action === "reject") {
                await this._rejectApplication(application.id);
            }
        } finally {
            this._isHandlingApplication = false;
        }
    }

    private async _rejectApplication(applicationId: number) {
        try {
            await rejectApplication(applicationId);
            void this._applicationsTask.run();
        } catch (error) {
            void alert(error instanceof Error ? error.message : "Bewerbung konnte nicht abgelehnt werden.", {
                type: "warning",
            });
        }
    }

    private async _acceptApplication(applicationId: number, employeeId: number) {
        try {
            await acceptApplication(applicationId, employeeId);
            void this._applicationsTask.run();
        } catch (error) {
            void alert(error instanceof Error ? error.message : "Bewerbung konnte nicht übernommen werden.", {
                type: "warning",
            });
        }
    }

    private _handleFiltersChange(event: CustomEvent) {
        const target = event.target as EntityFiltersEl;
        const filters = [...target.filters];
        this._filters = filters;
        this._persistFilters(filters);
    }

    private _clearFilters() {
        this._filters = [];
        this._persistFiltersNow([]);
    }

    private _renderGroup(group: ApplicationGroup) {
        return html`
            <article class="posting">
                <header class="posting-header">
                    <h3 class="posting-title" title=${group.posting.title}>${group.posting.title}</h3>
                    <div class="posting-details small pills">
                        <div class="posting-detail orange pill">
                            <ptc-ui-icon name="icon.object.application-photo"></ptc-ui-icon>
                            ${group.applications.length}
                            ${group.applications.length === 1 ? "Bewerbung" : "Bewerbungen"}
                        </div>

                        <div class="posting-detail pill">
                            <ptc-ui-icon name="icon.object.time"></ptc-ui-icon>
                            ${group.posting.start ? `Ab ${formatDate(group.posting.start)}` : "Ab sofort"}
                        </div>

                        ${(group.posting.positions || []).map(
                            (position) => html`
                                <div class="pill" style="--color-highlight: ${app.getPositionColor(position)}">
                                    <ptc-ui-icon name="icon.object.position"></ptc-ui-icon>
                                    ${position.name}
                                </div>
                            `
                        )}
                    </div>

                    <button
                        class="posting-detail pill small posting-open"
                        @click=${() => router.go(`recruiting/${group.posting.id}`)}
                        title="Stelle ansehen"
                    >
                        <ptc-ui-icon name="icon.navigation.external-link"></ptc-ui-icon>
                        Stelle ansehen
                    </button>
                </header>
                <div class="applications">
                    ${group.applications.map(
                        (application) => html`
                            <ptc-job-application-preview
                                .application=${application}
                                @application-click=${() => this._openApplication(group, application)}
                            ></ptc-job-application-preview>
                        `
                    )}
                </div>
            </article>
        `;
    }

    render() {
        return html`
            <ptc-dashboard-tile
                label=${DASHBOARD_CONFIG.tiles.recruitingApplications.label}
                .subLabel=${this._subLabel}
                icon="icon.object.recruiting"
                .size=${this.size}
                style="--tile-icon-color: var(--orange)"
                tileType="recruitingApplications"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                <fieldset slot="settings">
                    <legend>Filter</legend>
                    <ptc-entity-filters
                        .filters=${this._filters}
                        .filterTypes=${["venue", "position"] as EntityFilter["type"][]}
                        hideEmployeeCount
                        emptyLabel="Alle Bewerbungen"
                        @change=${this._handleFiltersChange}
                    ></ptc-entity-filters>
                </fieldset>
                <div class="content">
                    ${this._applicationsTask.render({
                        initial: () => html``,
                        pending: () => html`
                            <ptc-ui-spinner
                                class="double-padded place-self-center"
                                active
                                style="font-size: 1rem"
                            ></ptc-ui-spinner>
                        `,
                        error: () => html`
                            <div class="empty-state">
                                <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                                <div class="empty-title">Bewerbungen konnten nicht geladen werden</div>
                            </div>
                        `,
                        complete: (allGroups) => {
                            const groups = this._applyFilters(allGroups);
                            if (!groups.length) {
                                const filtersActive = this._filters.length > 0;
                                return html`
                                    <div class="empty-state">
                                        <ptc-ui-icon name="icon.status.empty"></ptc-ui-icon>
                                        <div class="empty-title">
                                            ${filtersActive
                                                ? "Keine Bewerbungen für die aktuellen Filter"
                                                : "Keine offenen Bewerbungen"}
                                        </div>
                                        <div>
                                            ${filtersActive
                                                ? "Passe die Filter an, um mehr Bewerbungen zu sehen."
                                                : "Neue Bewerbungen erscheinen hier direkt für deinen Zuständigkeitsbereich."}
                                        </div>
                                        ${filtersActive
                                            ? html`
                                                  <button class="ghost slim" @click=${this._clearFilters}>
                                                      Filter zurücksetzen
                                                  </button>
                                              `
                                            : ""}
                                    </div>
                                `;
                            }

                            return html`<div class="list">${groups.map((group) => this._renderGroup(group))}</div>`;
                        },
                    })}
                </div>
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-recruiting": DashboardTileRecruiting;
    }
}
